
import React, { useState, useEffect } from "react";
import {
    BarChart3,
    CheckCircle,
    XCircle,
    ListChecks,
    Briefcase,
    Shuffle,
    Grid,
    Type,
    X
} from "lucide-react"; // nhớ import thêm icon nếu chưa có
import axios from "../routers/axios";
import { toast } from "react-toastify"; // ✅ Thêm dòng này

/**
 * AddQuestionComponent
 * - Giữ nguyên toàn bộ logic tạo câu hỏi từ bản gốc
 * - BỔ SUNG: mode = create | edit | view, existingQuestion, onQuestionUpdated
 * - Khi view: disable toàn bộ input + ẩn nút lưu
 * - Khi edit: load dữ liệu từ existingQuestion và gửi PUT /questions/:id
 * - Khi create: giữ nguyên POST /questions/create + tính question_number tự động theo scenario
 */
const AddQuestionComponent = ({
    testId,
    scenarioId,
    type, // "reading" | "writing" (section)
    mode = "create", // "create" | "edit" | "view"
    existingQuestion = null,
    questionType = "multiple_choice", // ✅ nhận loại câu hỏi từ tab hiện tại
    onClose,
    onQuestionAdded,
    onQuestionUpdated,
}) => {
    const isViewMode = mode === "view";
    const isEditMode = mode === "edit";

    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState({
        question_type: questionType || "multiple_choice", // ✅ đặt mặc định
        question_text: "",
        instructions: "",
        points: 1,
        options: [
            { option_label: "A", option_text: "", is_correct: false, position: 1 },
            { option_label: "B", option_text: "", is_correct: false, position: 2 },
            { option_label: "C", option_text: "", is_correct: false, position: 3 },
            { option_label: "D", option_text: "", is_correct: false, position: 4 },
        ],
    });
    const [errors, setErrors] = useState({});

    // modal cảnh báo
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [pendingType, setPendingType] = useState(null);
    const [actionType, setActionType] = useState(null); // "changeType" | "close"

    // Khi ở chế độ edit/view: load dữ liệu vào form
    useEffect(() => {
        if (existingQuestion && (isEditMode || isViewMode)) {
            setFormData({
                question_type: existingQuestion.question_type || "multiple_choice",
                question_text: existingQuestion.question_text || "",
                instructions: existingQuestion.instruction || "",
                points: existingQuestion.points ?? 1,
                options:
                    (existingQuestion.options || []).map((opt, idx) => ({
                        option_label: opt.option_key ?? String.fromCharCode(65 + idx),
                        option_text: opt.option_text ?? "",
                        is_correct: !!opt.is_correct,
                        position: opt.option_order ?? idx + 1,
                    })) || [],
            });
        }
    }, [existingQuestion, isEditMode, isViewMode]);
    // 🟢 Đồng bộ loại câu hỏi khi mở modal tạo mới (được mở từ tab khác)
    useEffect(() => {
        if (mode === "create") {
            setFormData((prev) => ({
                ...prev,
                question_type: questionType || "multiple_choice",
            }));
        }
    }, [questionType, mode]);


    const questionTypes = [
        { value: "multiple_choice", label: "Multiple Choice", icon: BarChart3 },
        { value: "true_false", label: "True / False", icon: CheckCircle },
        { value: "true_false_not_given", label: "True / False / Not Given", icon: XCircle },
        { value: "classification", label: "Classification", icon: ListChecks },
        { value: "job_comparison", label: "Job Comparison", icon: Briefcase },
        { value: "matching", label: "Matching", icon: Shuffle },
        { value: "synthesis_matrix", label: "Synthesis Matrix", icon: Grid },
        { value: "short_answer", label: "Short Answer", icon: Type },
    ];

    const handleInputChange = (field, value) => {
        if (isViewMode) return;
        setFormData((prev) => ({
            ...prev,
            [field]: value,
        }));
        if (errors[field]) {
            setErrors((prev) => ({
                ...prev,
                [field]: "",
            }));
        }
    };

    // đổi loại câu hỏi
    const handleTypeClick = (newType) => {
        if (isViewMode) return;
        if (newType === formData.question_type) return;

        const hasInput =
            formData.question_text.trim() !== "" ||
            formData.instructions.trim() !== "" ||
            formData.options.some((o) => o.option_text.trim() !== "");

        if (hasInput) {
            setPendingType(newType);
            setActionType("changeType");
            setShowConfirmModal(true);
        } else {
            applyTypeChange(newType);
        }
    };

    const applyTypeChange = (newType) => {
        setFormData((prev) => {
            const base = {
                ...prev,
                question_type: newType,
                question_text: "",
                instructions: "",
            };

            if (newType === "multiple_choice") {
                base.options = [
                    { option_label: "A", option_text: "", is_correct: false, position: 1 },
                    { option_label: "B", option_text: "", is_correct: false, position: 2 },
                    { option_label: "C", option_text: "", is_correct: false, position: 3 },
                    { option_label: "D", option_text: "", is_correct: false, position: 4 },
                ];
            } else if (newType === "reading_comprehension") {
                base.options = [
                    { option_label: "Blank1", option_text: "", is_correct: false, position: 1 },
                ];
            } else if (newType === "true_false") {
                base.options = [
                    { option_label: "True", option_text: "True", is_correct: false, position: 1 },
                    { option_label: "False", option_text: "False", is_correct: false, position: 2 },
                ];
            } else if (newType === "essay") {
                base.options = [];
            }

            return base;
        });
    };

    // nhấn X hoặc Hủy bỏ
    const handleClose = () => {
        const hasInput =
            formData.question_text.trim() !== "" ||
            formData.instructions.trim() !== "" ||
            formData.options.some((o) => o.option_text.trim() !== "");

        // Khi tạo mới: cảnh báo nếu có dữ liệu nhập
        if (mode === "create" && hasInput && !isViewMode) {
            setActionType("close");
            setShowConfirmModal(true);
            return;
        }

        // Khi sửa: chỉ cảnh báo nếu có thay đổi so với existingQuestion
        if (isEditMode && existingQuestion) {
            const originalData = {
                question_type: existingQuestion.question_type || "multiple_choice",
                question_text: existingQuestion.question_text || "",
                instructions: existingQuestion.instruction || "",
                points: existingQuestion.points ?? 1,
                options:
                    (existingQuestion.options || []).map((opt, idx) => ({
                        option_label: opt.option_key ?? String.fromCharCode(65 + idx),
                        option_text: opt.option_text ?? "",
                        is_correct: !!opt.is_correct,
                        position: opt.option_order ?? idx + 1,
                    })) || [],
            };

            const isChanged = JSON.stringify(formData) !== JSON.stringify(originalData);
            if (isChanged) {
                setActionType("close");
                setShowConfirmModal(true);
                return;
            }
        }

        // Nếu không có gì để cảnh báo thì đóng luôn
        if (onClose) onClose();
    };



    const confirmTypeChange = () => {
        if (actionType === "changeType" && pendingType) {
            applyTypeChange(pendingType);
            setPendingType(null);
        } else if (actionType === "close") {
            if (onClose) onClose();
        }
        setActionType(null);
        setShowConfirmModal(false);
    };

    const cancelTypeChange = () => {
        setPendingType(null);
        setActionType(null);
        setShowConfirmModal(false);
    };

    const handleOptionChange = (index, field, value) => {
        if (isViewMode) return;
        const newOptions = [...formData.options];
        newOptions[index] = { ...newOptions[index], [field]: value };
        setFormData((prev) => ({ ...prev, options: newOptions }));
    };

    const addOption = () => {
        if (isViewMode) return;
        const newLabel = String.fromCharCode(65 + formData.options.length);
        setFormData((prev) => ({
            ...prev,
            options: [
                ...prev.options,
                {
                    option_label: newLabel,
                    option_text: "",
                    is_correct: false,
                    position: prev.options.length + 1,
                },
            ],
        }));
    };

    const removeOption = (index) => {
        if (isViewMode) return;
        if (formData.options.length > 1) {
            const newOptions = formData.options.filter((_, i) => i !== index);
            newOptions.forEach((opt, i) => {
                opt.option_label = String.fromCharCode(65 + i);
                opt.position = i + 1;
            });
            setFormData((prev) => ({ ...prev, options: newOptions }));
        }
    };

    const validateForm = () => {
        const newErrors = {};
        if (!formData.question_text.trim()) {
            newErrors.question_text = "Vui lòng nhập nội dung câu hỏi";
        }

        if (formData.question_type === "multiple_choice") {
            if (!formData.options.some((opt) => opt.is_correct)) {
                newErrors.correct_answer = "Vui lòng chọn đáp án đúng";
            }
        }

        if (formData.question_type === "reading_comprehension") {
            if (formData.options.some((opt) => !opt.option_text.trim())) {
                newErrors.options = "Vui lòng nhập đầy đủ đáp án cho các chỗ trống";
            }
        }

        if (formData.question_type === "true_false") {
            if (!formData.options.some((opt) => opt.is_correct)) {
                newErrors.correct_answer = "Vui lòng chọn True hoặc False là đáp án đúng";
            }
        }

        if (formData.points < 1) {
            newErrors.points = "Điểm số phải lớn hơn 0";
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const buildOptionsPayload = (options) =>
        (options || []).map((opt, i) => ({
            option_key: opt.option_label || `O${i + 1}`,
            option_text: opt.option_text,
            is_correct: !!opt.is_correct,
            option_order: opt.position || i + 1,
        }));

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (isViewMode) return;
        if (!validateForm()) return;

        setLoading(true);
        try {
            // ===========================
            // 🔹 CASE 1: CẬP NHẬT CÂU HỎI
            // ===========================
            if (isEditMode && existingQuestion) {
                const apiData = {
                    scenario_id: existingQuestion.scenario_id ?? null,
                    question_number: existingQuestion.question_number,
                    question_type: formData.question_type,
                    section: existingQuestion.section ?? type,
                    instruction: formData.instructions || null,
                    question_text: formData.question_text,
                    word_bank: formData.word_bank || null,
                    correct_answer: formData.correct_answer || null,
                    alternative_answers: formData.alternative_answers || null,
                    answer_explanation: formData.answer_explanation || null,
                    feedback: formData.feedback || null,
                    points: formData.points || 1.0,
                    options: buildOptionsPayload(formData.options),
                };

                const res = await axios.put(
                    `/questions/update/${existingQuestion.id}`,
                    apiData
                );
                toast.success("✅ Cập nhật câu hỏi thành công!"); // ✅ Thông báo update
                if (onQuestionUpdated) onQuestionUpdated(res.data);
                if (onClose) onClose();
                return;
            }

            // ===========================
            // 🔹 CASE 2: TẠO CÂU HỎI MỚI
            // ===========================
            // Phân biệt 2 luồng:
            // - Tạo trong Bank → không gửi scenario_id, section
            // - Tạo trong ScenarioDetail → có scenarioId & type
            let apiData = {
                question_type: formData.question_type,
                question_text: formData.question_text,
                instruction: formData.instructions || null,
                word_bank: formData.word_bank || null,
                correct_answer: formData.correct_answer || null,
                alternative_answers: formData.alternative_answers || null,
                answer_explanation: formData.answer_explanation || null,
                feedback: formData.feedback || null,
                points: formData.points || 1.0,
                options: buildOptionsPayload(formData.options),
            };

            // ✅ Nếu đang trong ScenarioDetail (có scenarioId & type)
            if (scenarioId && type) {
                // Lấy danh sách câu hỏi hiện có để tính question_number
                const listRes = await axios.get(`/questions/list?scenario_id=${scenarioId}`);
                const currentQuestions = listRes.data || [];
                const nextNumber =
                    currentQuestions.length > 0
                        ? Math.max(...currentQuestions.map((q) => q.question_number)) + 1
                        : 1;

                apiData = {
                    ...apiData,
                    scenario_id: scenarioId,
                    question_number: nextNumber,
                    section: type, // reading hoặc writing
                };
            }

            const res = await axios.post("/questions/create", apiData);
            toast.success("✅ Câu hỏi đã được tạo thành công!"); // ✅ Thông báo create

            if (onQuestionAdded) {
                onQuestionAdded(res.data);
            }

            // ✅ Nếu bạn muốn sau khi tạo xong vẫn ở lại form:
            // reset formData nhưng KHÔNG đóng modal
            setFormData({
                ...formData,
                question_text: "",
                instructions: "",
                points: 1,
                options: [],
                word_bank: null,
                correct_answer: null,
                alternative_answers: null,
                answer_explanation: null,
                feedback: null,
            });

            // ⚠️ Bỏ dòng onClose() để KHÔNG đóng modal
            // if (onClose) onClose();

        } catch (error) {
            console.error("Error adding/updating question:", error);
            toast.error("❌ Có lỗi xảy ra khi lưu câu hỏi!"); // ✅ Thông báo lỗi
        } finally {
            setLoading(false);
        }
    };


    return (
        <>
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm overflow-y-auto">
                <div className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden max-w-5xl w-full my-8 relative">
                {/* Header */}
                <div className="flex items-center justify-between p-4 bg-blue-50 border-b">
                    <h3 className="text-lg font-semibold text-gray-900">
                        {isEditMode ? "✏️ Sửa câu hỏi" : isViewMode ? "👁️ Xem câu hỏi" : "➕ Thêm câu hỏi mới"}
                    </h3>
                    <button
                        onClick={handleClose}
                        className="p-2 hover:bg-blue-100 rounded-lg transition-colors"
                    >
                        <X className="w-5 h-5 text-gray-600" />
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="p-6 space-y-6">
                    {/* Tabs chọn loại câu hỏi */}
                    <div className="border-b mb-4 flex gap-4 overflow-x-auto no-scrollbar">
                        {questionTypes.map((qt) => {
                            const Icon = qt.icon;
                            const isActive = formData.question_type === qt.value;
                            return (
                                <button
                                    key={qt.value}
                                    type="button"
                                    onClick={() => handleTypeClick(qt.value)}
                                    disabled={isViewMode}
                                    className={`flex items-center gap-2 pb-2 whitespace-nowrap border-b-2 transition-all ${isActive
                                        ? "border-blue-600 text-blue-600 font-semibold"
                                        : "border-transparent text-gray-600 hover:text-blue-600"
                                        } ${isViewMode ? "opacity-60 cursor-not-allowed" : ""}`}
                                >
                                    <Icon className="w-4 h-4" />
                                    <span>{qt.label}</span>
                                </button>
                            );
                        })}
                    </div>
                    {/* Điểm số */}
                    <div>
                        <label className="block text-sm font-medium">Điểm số</label>
                        <input
                            type="number"
                            min="1"
                            value={formData.points}
                            onChange={(e) =>
                                handleInputChange("points", parseInt(e.target.value) || 1)
                            }
                            disabled={isViewMode}
                            className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${errors.points ? "border-red-500" : "border-gray-300"
                                }`}
                        />
                        {errors.points && (
                            <p className="text-sm text-red-600">{errors.points}</p>
                        )}
                    </div>

                    {/* Nội dung câu hỏi */}
                    <div>
                        <label className="block text-sm font-medium">Nội dung *</label>
                        <textarea
                            value={formData.question_text}
                            onChange={(e) => handleInputChange("question_text", e.target.value)}
                            disabled={isViewMode}
                            rows={4}
                            className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${errors.question_text ? "border-red-500" : "border-gray-300"
                                }`}
                        />
                        {errors.question_text && (
                            <p className="text-sm text-red-600">{errors.question_text}</p>
                        )}
                    </div>

                    {/* Instructions */}
                    <div>
                        <label className="block text-sm font-medium">
                            Hướng dẫn (tùy chọn)
                        </label>
                        <textarea
                            value={formData.instructions}
                            onChange={(e) => handleInputChange("instructions", e.target.value)}
                            disabled={isViewMode}
                            rows={2}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        />
                    </div>

                    {/* Multiple Choice */}
                    {formData.question_type === "multiple_choice" && (
                        <div className="space-y-4">
                            <label className="block text-sm font-medium">
                                Các lựa chọn (A, B, C, D...)
                            </label>
                            {formData.options.map((opt, idx) => (
                                <div key={idx} className="flex gap-2 items-center">
                                    <input
                                        type="radio"
                                        name="correct_answer"
                                        disabled={isViewMode}
                                        checked={opt.is_correct}
                                        onChange={() => {
                                            const newOpts = formData.options.map((o, i) => ({
                                                ...o,
                                                is_correct: i === idx,
                                            }));
                                            setFormData((prev) => ({ ...prev, options: newOpts }));
                                        }}
                                    />
                                    <span className="w-6">{opt.option_label}.</span>
                                    <input
                                        type="text"
                                        value={opt.option_text}
                                        onChange={(e) =>
                                            handleOptionChange(idx, "option_text", e.target.value)
                                        }
                                        disabled={isViewMode}
                                        className="flex-1 px-3 py-2 border rounded-lg"
                                        placeholder={`Lựa chọn ${opt.option_label}`}
                                    />
                                    {!isViewMode && formData.options.length > 2 && (
                                        <button
                                            type="button"
                                            onClick={() => removeOption(idx)}
                                            className="text-red-500"
                                        >
                                            <X className="w-4 h-4" />
                                        </button>
                                    )}
                                </div>
                            ))}
                            {!isViewMode && (
                                <button
                                    type="button"
                                    onClick={addOption}
                                    className="text-blue-600 text-sm"
                                >
                                    + Thêm lựa chọn
                                </button>
                            )}
                            {errors.correct_answer && (
                                <p className="text-sm text-red-600">{errors.correct_answer}</p>
                            )}
                        </div>
                    )}

                    {/* True / False */}
                    {formData.question_type === "true_false" && (
                        <div className="space-y-2">
                            <label className="block text-sm font-medium">Chọn đáp án đúng</label>
                            {["True", "False"].map((val, idx) => (
                                <div key={val} className="flex gap-2 items-center">
                                    <input
                                        type="radio"
                                        name="true_false"
                                        disabled={isViewMode}
                                        checked={formData.options[idx]?.is_correct || false}
                                        onChange={() => {
                                            const newOptions = [
                                                {
                                                    option_label: "True",
                                                    option_text: "True",
                                                    is_correct: val === "True",
                                                    position: 1,
                                                },
                                                {
                                                    option_label: "False",
                                                    option_text: "False",
                                                    is_correct: val === "False",
                                                    position: 2,
                                                },
                                            ];
                                            setFormData((prev) => ({ ...prev, options: newOptions }));
                                        }}
                                    />
                                    <span>{val}</span>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* True / False / Not Given */}
                    {formData.question_type === "true_false_not_given" && (
                        <div className="space-y-2">
                            <label className="block text-sm font-medium">Chọn đáp án đúng</label>
                            {["True", "False", "Not Given"].map((val, idx) => (
                                <div key={val} className="flex gap-2 items-center">
                                    <input
                                        type="radio"
                                        name="true_false_ng"
                                        disabled={isViewMode}
                                        checked={formData.options[idx]?.is_correct || false}
                                        onChange={() => {
                                            const newOptions = ["True", "False", "Not Given"].map((v) => ({
                                                option_label: v,
                                                option_text: v,
                                                is_correct: v === val,
                                            }));
                                            setFormData((prev) => ({ ...prev, options: newOptions }));
                                        }}
                                    />
                                    <span>{val}</span>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Classification */}
                    {formData.question_type === "classification" && (
                        <div className="space-y-4">
                            <label className="block text-sm font-medium">Các nhóm phân loại</label>
                            {formData.categories?.map((cat, idx) => (
                                <div key={idx} className="flex gap-2 items-center">
                                    <input
                                        type="text"
                                        value={cat}
                                        onChange={(e) => {
                                            const cats = [...formData.categories];
                                            cats[idx] = e.target.value;
                                            setFormData({ ...formData, categories: cats });
                                        }}
                                        disabled={isViewMode}
                                        className="flex-1 px-3 py-2 border rounded-lg"
                                        placeholder={`Tên nhóm ${idx + 1}`}
                                    />
                                </div>
                            ))}
                            {!isViewMode && (
                                <button
                                    type="button"
                                    onClick={() =>
                                        setFormData({
                                            ...formData,
                                            categories: [...(formData.categories || []), ""],
                                        })
                                    }
                                    className="text-blue-600 text-sm"
                                >
                                    + Thêm nhóm
                                </button>
                            )}
                        </div>
                    )}

                    {/* Job Comparison */}
                    {formData.question_type === "job_comparison" && (
                        <div className="space-y-4">
                            <label className="block text-sm font-medium">Mô tả công việc A</label>
                            <textarea
                                className="w-full border rounded-lg p-2"
                                value={formData.jobA_text || ""}
                                onChange={(e) =>
                                    setFormData({ ...formData, jobA_text: e.target.value })
                                }
                                disabled={isViewMode}
                            />
                            <label className="block text-sm font-medium">Mô tả công việc B</label>
                            <textarea
                                className="w-full border rounded-lg p-2"
                                value={formData.jobB_text || ""}
                                onChange={(e) =>
                                    setFormData({ ...formData, jobB_text: e.target.value })
                                }
                                disabled={isViewMode}
                            />
                        </div>
                    )}

                    {/* Matching */}
                    {formData.question_type === "matching" && (
                        <div className="space-y-3">
                            <label className="block text-sm font-medium">Ghép cặp câu hỏi – đáp án</label>
                            {formData.pairs?.map((pair, idx) => (
                                <div key={idx} className="flex gap-2 items-center">
                                    <input
                                        type="text"
                                        value={pair.question}
                                        onChange={(e) => {
                                            const newPairs = [...formData.pairs];
                                            newPairs[idx].question = e.target.value;
                                            setFormData({ ...formData, pairs: newPairs });
                                        }}
                                        placeholder="Câu hỏi"
                                        className="flex-1 px-3 py-2 border rounded-lg"
                                    />
                                    <input
                                        type="text"
                                        value={pair.answer}
                                        onChange={(e) => {
                                            const newPairs = [...formData.pairs];
                                            newPairs[idx].answer = e.target.value;
                                            setFormData({ ...formData, pairs: newPairs });
                                        }}
                                        placeholder="Đáp án"
                                        className="flex-1 px-3 py-2 border rounded-lg"
                                    />
                                </div>
                            ))}
                            {!isViewMode && (
                                <button
                                    type="button"
                                    onClick={() =>
                                        setFormData({
                                            ...formData,
                                            pairs: [...(formData.pairs || []), { question: "", answer: "" }],
                                        })
                                    }
                                    className="text-blue-600 text-sm"
                                >
                                    + Thêm cặp ghép
                                </button>
                            )}
                        </div>
                    )}

                    {/* Synthesis Matrix */}
                    {formData.question_type === "synthesis_matrix" && (
                        <div className="space-y-4">
                            <label className="block text-sm font-medium">Bảng Synthesis Matrix</label>
                            <p className="text-sm text-gray-500">
                                Nhập các hàng và cột để tạo ma trận Yes/No.
                            </p>
                            <textarea
                                rows={3}
                                placeholder="Ví dụ: Nhập hàng cách nhau bằng dấu phẩy"
                                className="w-full border rounded-lg p-2"
                                value={formData.rows?.join(", ") || ""}
                                onChange={(e) =>
                                    setFormData({
                                        ...formData,
                                        rows: e.target.value.split(",").map((s) => s.trim()),
                                    })
                                }
                            />
                            <textarea
                                rows={3}
                                placeholder="Nhập cột cách nhau bằng dấu phẩy"
                                className="w-full border rounded-lg p-2"
                                value={formData.columns?.join(", ") || ""}
                                onChange={(e) =>
                                    setFormData({
                                        ...formData,
                                        columns: e.target.value.split(",").map((s) => s.trim()),
                                    })
                                }
                            />
                        </div>
                    )}

                    {/* Short Answer */}
                    {formData.question_type === "short_answer" && (
                        <div className="space-y-4">
                            <label className="block text-sm font-medium">Đáp án mong đợi</label>
                            <textarea
                                rows={3}
                                placeholder="Câu trả lời mẫu..."
                                className="w-full border rounded-lg p-2"
                                value={formData.expected_answer || ""}
                                onChange={(e) =>
                                    setFormData({ ...formData, expected_answer: e.target.value })
                                }
                            />
                            <label className="block text-sm font-medium">
                                Hướng dẫn chấm điểm (tùy chọn)
                            </label>
                            <textarea
                                rows={2}
                                placeholder="Gợi ý cho người chấm..."
                                className="w-full border rounded-lg p-2"
                                value={formData.marking_guide || ""}
                                onChange={(e) =>
                                    setFormData({ ...formData, marking_guide: e.target.value })
                                }
                            />
                        </div>
                    )}

                    {/* Buttons */}
                    <div className="flex gap-3 pt-4 border-t">
                        <button
                            type="button"
                            onClick={handleClose}
                            className="px-6 py-2 border rounded-lg"
                        >
                            {isViewMode ? "Đóng" : "Hủy bỏ"}
                        </button>
                        {!isViewMode && (
                            <button
                                type="submit"
                                disabled={loading}
                                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                            >
                                {loading ? "Đang lưu..." : isEditMode ? "Cập nhật" : "Lưu câu hỏi"}
                            </button>
                        )}
                    </div>
                </form>
                </div>
            </div>

            {/* Confirm Modal */}
            {showConfirmModal && (
                <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
                    <div className="bg-white rounded-lg shadow-lg p-6 max-w-md w-full relative">
                        {/* Nút X */}
                        <button
                            onClick={cancelTypeChange}
                            className="absolute top-3 right-3 p-2 rounded-full hover:bg-gray-100"
                        >
                            <X className="w-5 h-5 text-gray-500" />
                        </button>

                        <h2 className="text-lg font-semibold text-gray-800 mb-4">
                            {actionType === "close"
                                ? "Xác nhận thoát"
                                : "Xác nhận đổi loại câu hỏi"}
                        </h2>
                        <p className="text-gray-600 mb-6">
                            {actionType === "close"
                                ? "Bạn đã nhập dữ liệu. Thoát ra sẽ làm mất dữ liệu chưa lưu. Bạn có chắc chắn muốn thoát?"
                                : "Bạn đã nhập dữ liệu. Đổi loại câu hỏi sẽ làm mất dữ liệu cũ. Bạn có chắc chắn muốn tiếp tục?"}
                        </p>
                        <div className="flex justify-end gap-3">
                            <button
                                onClick={cancelTypeChange}
                                className="px-4 py-2 border rounded-lg"
                            >
                                Hủy
                            </button>
                            <button
                                onClick={confirmTypeChange}
                                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                            >
                                Tiếp tục
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default AddQuestionComponent;
